import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import drRicardoLopez from "@/assets/dr-ricardo-lopez.jpg";

const DrSection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    
    if (prefersReducedMotion) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="relative overflow-hidden bg-secondary/30"
      aria-label="Dr. Ricardo López - Información del profesional"
    >
      {/* Hero Container with controlled height */}
      <div className="relative py-12 sm:py-16 md:py-0 md:h-[360px] lg:h-[400px]">
        {/* Content Container */}
        <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-16 h-full flex flex-col md:flex-row items-center gap-6 sm:gap-8 md:gap-12">
          {/* Circular Photo - Left Side */}
          <div 
            className={`flex-shrink-0 transition-all duration-700 ease-out ${
              isVisible 
                ? 'opacity-100 scale-100' 
                : 'opacity-0 scale-95'
            }`}
          >
            <div className="w-[160px] h-[160px] sm:w-[200px] sm:h-[200px] md:w-[260px] md:h-[260px] lg:w-[280px] lg:h-[280px] rounded-full overflow-hidden border-4 border-primary/20 shadow-xl hover:border-primary/40 transition-all duration-300">
              <img
                src={drRicardoLopez}
                alt="Dr. Ricardo López - Dentist with professional dental equipment"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center 15%' }}
                loading="lazy"
                width={280}
                height={280}
                decoding="async"
              />
            </div>
          </div>

          {/* Text Content - Right Side */}
          <div 
            className={`flex-1 space-y-2 sm:space-y-3 md:space-y-4 text-center md:text-left transition-all duration-700 ease-out delay-100 ${
              isVisible 
                ? 'opacity-100 translate-x-0' 
                : 'opacity-0 translate-x-6'
            }`}
          >
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
              {t('drSection.title')}
            </h2>
            <div className="w-16 sm:w-20 h-1 bg-primary mx-auto md:mx-0" />
            <p className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold text-primary">
              {t('drSection.specialty')}
            </p>
            <p className="text-sm sm:text-base md:text-base lg:text-lg text-muted-foreground max-w-xl leading-relaxed">
              {t('drSection.description')}
            </p>
            <Link to="/our-clinic" className="inline-block">
              <Button 
                size="lg"
                className="mt-2 sm:mt-3 md:mt-4 group hover:scale-105 transition-transform duration-300"
              >
                {t('nav.ourClinic')}
                <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300">→</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DrSection;
